import {
  Box,
  Button,
  Checkbox,
  Flex,
  Spacer,
  Td,
  Text,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import {
  Column,
  ColumnFiltersState,
  Row,
  Table as ReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import {
  date as commonDate,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { ReceivableBorrowingBaseTestWaiverInputGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { FiFilter } from 'react-icons/fi';

const updatedAtColumnId: keyof ReceivableBorrowingBaseTestWaiverInputGQLType =
  'updated_at';
const startAndEndDateColumnIds: (keyof ReceivableBorrowingBaseTestWaiverInputGQLType)[] = [
  'waiverStartReportingDate',
  'waiverExpirationReportingDate',
];

export const renderSelectCell = ({
  row,
}: {
  row: Row<ReceivableBorrowingBaseTestWaiverInputGQLType>;
}) => {
  return (
    <Box textAlign="center">
      <Checkbox
        isChecked={row.getIsSelected()}
        isDisabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
      />
    </Box>
  );
};

export const ColumnFilter = ({
  column,
  currentColumnFilters,
  table,
}: {
  column: Column<ReceivableBorrowingBaseTestWaiverInputGQLType>;
  currentColumnFilters: ColumnFiltersState;
  table: ReactTable<ReceivableBorrowingBaseTestWaiverInputGQLType>;
}) => {
  const sortedUniqueValues = useMemo(() => {
    const values = table
      .getCoreRowModel()
      .flatRows.map((row) => row.getValue(column.id))
      .sort() as string[];
    return Array.from(new Set(values));
  }, [table, column.id]);

  const options = useMemo(() => {
    return _.map(sortedUniqueValues, (value) => {
      let label: string = commonUtils.toTitleCase(value);
      if (column.id === updatedAtColumnId && typeof value === 'number') {
        label = `${commonDate.epochToSlashedDateStrWithTime(value)} EST`;
      } else if (
        _.includes(startAndEndDateColumnIds, column.id) &&
        commonTypeguards.isFullDateStr(value)
      ) {
        label = commonDate.fullDateStrToSlashedDateStr(value, 'M/d/yy');
      }

      return {
        label,
        value,
      };
    });
  }, [sortedUniqueValues, column.id]);

  const onApplyOptions = (values: string[]) => {
    column.setFilterValue(values);
  };
  const columnFilterValuesForColumn = _.filter(
    currentColumnFilters,
    ({ id }) => id === column.id,
  );

  let initialSelections = sortedUniqueValues;
  if (columnFilterValuesForColumn.length) {
    if (columnFilterValuesForColumn) {
      const columnFilterValues = columnFilterValuesForColumn[0].value as any[];
      initialSelections = _.map(columnFilterValues, (value) =>
        _.toString(value),
      );
    }
  }

  return (
    <IsolatedMultiSelect
      applyButtonText="Apply filters"
      iconButton={<FiFilter />}
      initialSelections={initialSelections}
      menuListProps={{
        mt: '16px',
        zIndex: 'dropdown',
      }}
      onApplyOptions={onApplyOptions}
      options={options}
      optionsHeader={commonUtils.toTitleCase(column.id)}
      titleText="Filter"
    />
  );
};

const customMultiFilter = (
  row: Row<ReceivableBorrowingBaseTestWaiverInputGQLType>,
  id: string,
  filterValues: Util.Primitive[],
) => {
  const rowValue: Util.Primitive = row.getValue(id);
  if (filterValues.length === 0 || filterValues.includes(rowValue)) {
    return true;
  }
  return false;
};

export const formatBaseColumnDef = ({
  accessorKey,
  header,
}: {
  accessorKey: keyof ReceivableBorrowingBaseTestWaiverInputGQLType;
  header: string;
}) => {
  return {
    id: accessorKey,
    accessorKey,
    header,
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: customMultiFilter,
  };
};

export const toggleCheckboxDataForAllRows = ({
  columnId,
  currentValue,
  setData,
  currentData,
}: {
  columnId: string;
  currentValue: boolean;
  setData: ({
    data,
  }: {
    data: ReceivableBorrowingBaseTestWaiverInputGQLType[];
  }) => void;
  currentData: ReceivableBorrowingBaseTestWaiverInputGQLType[];
}) => {
  const newData = _.map(currentData, (currentRow) => {
    return {
      ...currentRow!,
      [columnId]: !currentValue,
    };
  });
  setData({
    data: newData,
  });
};

export const renderCheckboxHeaderCell = ({
  tableInstance,
  column,
  setData,
  currentData,
}: {
  tableInstance: ReactTable<ReceivableBorrowingBaseTestWaiverInputGQLType>;
  column: Column<ReceivableBorrowingBaseTestWaiverInputGQLType, any>;
  setData: ({
    data,
  }: {
    data: ReceivableBorrowingBaseTestWaiverInputGQLType[];
  }) => void;
  currentData: ReceivableBorrowingBaseTestWaiverInputGQLType[];
}) => (
  <Box textAlign="center">
    <Checkbox
      isChecked={tableInstance.getIsAllRowsSelected()}
      isIndeterminate={tableInstance.getIsSomeRowsSelected()}
      onChange={(event) => {
        toggleCheckboxDataForAllRows({
          columnId: column.id,
          currentValue: tableInstance.getIsAllRowsSelected(),
          setData,
          currentData,
        });
        tableInstance.getToggleAllRowsSelectedHandler()(event);
      }}
    />
  </Box>
);

type ManageWaiverBulkActionRowProps = {
  numberOfSelectedWaivers: number;
  colSpan: number;
  onClickBulkEdit: () => void;
  onClickBulkDelete: () => void;
};
export const ManageWaiverBulkActionRow = ({
  numberOfSelectedWaivers,
  colSpan,
  onClickBulkEdit,
  onClickBulkDelete,
}: ManageWaiverBulkActionRowProps) => {
  const selectedWaiversInfoString =
    numberOfSelectedWaivers === 0
      ? 'No Waivers selected'
      : `${numberOfSelectedWaivers} Waiver${
          numberOfSelectedWaivers > 1 ? 's' : ''
        } selected`;

  return (
    <Tr backgroundColor={colors.gray[100]} height="60px">
      <Td colSpan={colSpan}>
        <Flex
          alignItems="center"
          height="24px"
          fontSize="14px"
          fontWeight="500"
        >
          <Text>{selectedWaiversInfoString}</Text>
          <Spacer />
          <Tooltip label="Edit selected waivers">
            <Button onClick={onClickBulkEdit} mr="4px">
              <Icon icon="edit" />
            </Button>
          </Tooltip>
          <Tooltip label="Delete selected waivers">
            <Button onClick={onClickBulkDelete}>
              <Icon icon="trash" />
            </Button>
          </Tooltip>
        </Flex>
      </Td>
    </Tr>
  );
};
